/**
 * Cache de cotizaciones (spec/03-data-model.md §2 "price_quote"). Vive solo
 * en el almacen local; nunca se escribe en la hoja del usuario y nunca forma
 * parte del libro (INV-4, INV-7). Borrar la cache entera no cambia un solo
 * saldo — es la prueba operativa de esos invariantes.
 */

import type { LocalStore } from "../storage/local-store.js";
import type { PriceQuote, QuoteProvider, QuoteRequest } from "./quote-provider.js";

const COLLECTION = "price_quotes";

function cacheKey(instrumentId: string, asOf: string): string {
  return `${instrumentId}|${asOf}`;
}

export class QuoteCache {
  constructor(
    private readonly store: LocalStore,
    private readonly provider: QuoteProvider,
  ) {}

  /**
   * Devuelve la cotizacion cacheada si existe; si no, intenta obtenerla del
   * proveedor y la cachea. Ante fallo del proveedor, degrada devolviendo lo
   * que haya en cache (o undefined) en vez de propagar el error (FR-MKT-4).
   */
  async getOrFetch(request: QuoteRequest): Promise<PriceQuote | undefined> {
    const cached = await this.store.get<PriceQuote>(COLLECTION, cacheKey(request.instrumentId, request.asOf));
    if (cached) return cached;

    try {
      const fetched = await this.provider.getQuote(request);
      if (fetched) {
        await this.store.putBatch(COLLECTION, [
          { id: cacheKey(fetched.instrumentId, fetched.asOf), value: fetched },
        ]);
      }
      return fetched;
    } catch {
      return undefined; // degradacion: sin cotizacion, nunca un error que bloquee la vista
    }
  }

  /** Permite al usuario introducir un precio manual que prevalece (FR-MKT-7). */
  async setManualQuote(quote: PriceQuote): Promise<void> {
    await this.store.putBatch(COLLECTION, [
      { id: cacheKey(quote.instrumentId, quote.asOf), value: { ...quote, manual: true } },
    ]);
  }

  /** Vacia toda la cache. Prueba operativa de INV-4/INV-7: ningun saldo depende de esto. */
  async clearAll(): Promise<void> {
    const items = await this.store.list<PriceQuote>(COLLECTION);
    for (const item of items) await this.store.delete(COLLECTION, item.id);
  }
}
